import React, { useState, useEffect } from 'react';
import { Link, useLocation, Outlet, Navigate } from 'react-router-dom';
import { driverApi, getToken } from '../../config/driverApi';
import { 
  LayoutDashboard, 
  ClipboardList, 
  Clock, 
  User, 
  LogOut, 
  Menu,
  X,
  Bell
} from 'lucide-react';

const DriverLayout = () => {
  const location = useLocation();
  const [driver, setDriver] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setDriver(null);
      return;
    }

    const fetchDriver = async () => {
      try {
        const res = await driverApi.get('/auth.php?action=profile');
        if (res.data.success) {
          setDriver(res.data.data);
        }
      } catch (err) {
        console.error('Failed to fetch driver profile:', err);
      }
    };

    fetchDriver();

    // Poll for notifications
    const interval = setInterval(async () => {
      try {
        const res = await driverApi.get('/notifications.php?action=notifications&unread_only=1');
        if (res.data.success) {
          setUnreadCount(res.data.unread_count || 0);
        }
      } catch (err) {
        console.error('Failed to fetch notifications:', err);
      }
    }, 30000); // Every 30 seconds

    return () => clearInterval(interval);
  }, []);

  // Poll Geolocation if Driver is Online
  useEffect(() => {
    if (!driver || driver.availability !== 'online') return;

    const sendLocation = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            try {
              await driverApi.post('/location.php', {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
              });
            } catch (err) {
              console.error('Failed to update GPS location:', err);
            }
          },
          (error) => {
            console.error('Geolocation error:', error);
          },
          { enableHighAccuracy: true, timeout: 10000 }
        );
      }
    };

    sendLocation();
    const locInterval = setInterval(sendLocation, 30000); // Every 30 seconds
    return () => clearInterval(locInterval);
  }, [driver?.availability]);

  const handleLogout = async () => {
    try {
      await driverApi.post('/auth.php?action=logout');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('driver_token');
      localStorage.removeItem('driver_info');
      window.location.href = '/driver/login';
    }
  };

  if (!getToken()) {
    return <Navigate to="/driver/login" replace />;
  }

  const navItems = [
    { path: '/driver/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/driver/orders', label: 'Orders', icon: ClipboardList },
    { path: '/driver/history', label: 'History', icon: Clock },
    { path: '/driver/notifications', label: 'Notifications', icon: Bell },
    { path: '/driver/profile', label: 'Profile', icon: User },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Mobile Header */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '56px',
        backgroundColor: '#1a1a1a',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 16px',
        zIndex: 1000,
      }}>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        <h1 style={{ fontSize: '1.1rem', fontWeight: 600, margin: 0 }}>Driver Portal</h1>
        <button
          onClick={handleLogout}
          style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}
        >
          <LogOut size={20} />
        </button>
      </div>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 999,
          }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '260px',
        height: '100vh',
        backgroundColor: '#1a1a1a',
        color: '#fff',
        padding: '20px 0',
        zIndex: 1001,
        transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
      }}>
        <div style={{ padding: '0 20px 20px', borderBottom: '1px solid #333' }}>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 700, margin: 0, marginBottom: '4px' }}>Syra Driver</h2>
          {driver && (
            <p style={{ fontSize: '0.85rem', color: '#999', margin: 0 }}>
              {driver.name}
            </p>
          )}
        </div>

        <nav style={{ flex: 1, padding: '10px 0' }}>
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 20px',
                  color: isActive(item.path) ? '#D4AF37' : '#ccc',
                  textDecoration: 'none',
                  backgroundColor: isActive(item.path) ? 'rgba(212,175,55,0.1)' : 'transparent',
                  borderLeft: isActive(item.path) ? '3px solid #D4AF37' : '3px solid transparent',
                  fontSize: '0.95rem',
                  fontWeight: isActive(item.path) ? 600 : 400,
                }}
              >
                <Icon size={20} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div style={{ padding: '10px 20px', borderTop: '1px solid #333' }}>
          <button
            onClick={handleLogout}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'none',
              border: 'none',
              color: '#ff6b6b',
              cursor: 'pointer',
              fontSize: '0.9rem',
              padding: '8px 0',
            }}
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ paddingTop: '56px', paddingBottom: '70px', minHeight: '100vh' }}>
        <Outlet context={{ driver, setDriver }} />
      </div>

      {/* Bottom Navigation (Mobile) */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: '60px',
        backgroundColor: '#fff',
        borderTop: '1px solid #e0e0e0',
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'center',
        zIndex: 1000,
      }}>
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '2px',
                color: active ? '#D4AF37' : '#999',
                textDecoration: 'none',
                fontSize: '0.65rem',
                padding: '4px 8px',
                position: 'relative',
              }}
            >
              <Icon size={22} />
              {item.label}
              {item.path === '/driver/notifications' && unreadCount > 0 && (
                <span style={{
                  position: 'absolute',
                  top: 0,
                  right: 4,
                  backgroundColor: '#ff4444',
                  color: '#fff',
                  borderRadius: '50%',
                  width: '16px',
                  height: '16px',
                  fontSize: '0.65rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 600,
                }}>
                  {unreadCount}
                </span>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default DriverLayout;
